/**
 * Canonical mapping from calendar cell state to display symbol metadata.
 *
 * Single source of truth so every rendering surface (CalendarGrid, PDF
 * legend, tooltips) stays consistent and cannot diverge.
 *
 * Symbol policy (E38-S12 / UAT 366-368 — reverses the E34-S8 override-dash decision):
 *   success     → ✓ (check)
 *   success + dateModified → ✓* (check + asterisk, E63-S1) — a success variant, not a CellState
 *   no_testing  → — (dash/minus)   the ONLY dash state
 *   override    → O (letter)       distinct glyph, legacy parity (was a dash)
 *   failure     → ✕ (X, red)
 *   confirmed   → ✕ (X, red badge) — only state that may render X
 *   day_off     → (blank/special)
 *   pending     → (blank)
 *
 * Labels are resolved from the canonical i18n source (`en.ts`) so wording lives
 * in ONE place — never hardcode a label/glyph here or in a legend.
 */

import { t } from '@/shared/lib/i18n'

export type CellState =
  | 'success'
  | 'failure'
  | 'no_testing'
  | 'pending'
  | 'override'
  | 'confirmed'
  | 'day_off'

export type SymbolKind =
  | 'check'
  | 'check_date_changed'
  | 'x'
  | 'x_badge'
  | 'dash'
  | 'letter_o'
  | 'none'

export interface CellSymbolDef {
  kind: SymbolKind
  /** Human-readable action label for tooltips and legends (canonical i18n). */
  label: string
}

export const CELL_SYMBOL_MAP: Record<CellState, CellSymbolDef> = {
  success: { kind: 'check', label: t('instruments.reports.cellSymbols.success') },
  failure: { kind: 'x', label: t('instruments.reports.cellSymbols.failure') },
  /** override (ODE) = "Override the Miss". Its own glyph — the letter O (legacy parity). */
  override: { kind: 'letter_o', label: t('instruments.reports.cellSymbols.override') },
  /** confirmed non-compliance (CNM). The only state shown with an X badge. */
  confirmed: { kind: 'x_badge', label: t('instruments.reports.cellSymbols.confirmed') },
  /** no-testing (NPT) = intentionally skipped. The only dash state. */
  no_testing: { kind: 'dash', label: t('instruments.reports.cellSymbols.noTesting') },
  pending: { kind: 'none', label: '' },
  day_off: { kind: 'none', label: t('instruments.reports.cellSymbols.dayOff') },
}

/** Complete (date changed): a completed task whose record date was modified. */
export const DATE_CHANGED_SUCCESS_SYMBOL: CellSymbolDef = {
  kind: 'check_date_changed',
  label: t('instruments.reports.cellSymbols.successDateChanged'),
}

export interface CellSymbolOptions {
  dateModified?: boolean
}

/**
 * Returns the symbol definition for a given cell state.
 * `dateModified` only affects `success` (→ ✓*); every other state ignores it.
 * Falls back to { kind: 'none', label: '' } for unknown states.
 */
export const getCellSymbol = (
  state: CellState,
  { dateModified = false }: CellSymbolOptions = {},
): CellSymbolDef => {
  if (state === 'success' && dateModified) return DATE_CHANGED_SUCCESS_SYMBOL
  return CELL_SYMBOL_MAP[state] ?? { kind: 'none', label: '' }
}

/** Returns true only for the states that can render an X icon. */
export const isXState = (state: CellState): boolean => state === 'failure' || state === 'confirmed'
